/**
 * Progression & « déjà vu » — localStorage versionné oc_v9 (migration douce depuis oc_v8).
 * Les clés sont namespacées par le PROFIL actif (`activeSuffix`) : l'invité utilise les
 * clés historiques (suffixe vide), chaque profil nommé a son propre espace.
 */
import type { Diff, GameId } from '@ordre-cache/shared';
import { NB_DEFIS } from '@ordre-cache/shared';
import { activeSuffix } from './profile';
import { scheduleSync } from './account';

type ProgMap = Record<string, Record<number, boolean[]>>;

let progress: ProgMap = {};

export function loadProg(): void {
  const suf = activeSuffix();
  try {
    // Profil nommé : sa clé uniquement. Invité : clé historique + migration oc_v8.
    const raw = suf
      ? localStorage.getItem('oc_v9' + suf)
      : (localStorage.getItem('oc_v9') ?? localStorage.getItem('oc_v8'));
    progress = JSON.parse(raw ?? '{}') as ProgMap;
  } catch {
    progress = {};
  }
}

export function saveProg(): void {
  try {
    localStorage.setItem('oc_v9' + activeSuffix(), JSON.stringify(progress));
  } catch {
    /* stockage indisponible : on ignore (mode dégradé) */
  }
  scheduleSync();
}

/** Tableau des 6 défis réussis pour (jeu, niveau) — créé à la volée (index.html:321). */
export function progOf(gid: GameId, diff: Diff): boolean[] {
  const g = (progress[gid] ??= {});
  return (g[diff] ??= new Array(NB_DEFIS).fill(false) as boolean[]);
}

/**
 * Parcours à déblocage : le défi 1 est toujours jouable ; ensuite il faut avoir
 * réussi le précédent. Un défi déjà réussi reste jouable (données historiques
 * d'avant le verrouillage comprises).
 */
export function isDefiUnlocked(gid: GameId, diff: Diff, defi: number): boolean {
  const prog = progOf(gid, diff);
  return defi === 0 || prog[defi] || prog[defi - 1];
}

/** « Front » de progression : premier défi non réussi — point de reprise naturel. */
export function frontierDefi(gid: GameId, diff: Diff): number {
  const prog = progOf(gid, diff);
  for (let i = 0; i < NB_DEFIS; i++) if (!prog[i]) return i;
  return NB_DEFIS - 1;
}

let seen: Record<string, number> = {};

export function loadSeen(): void {
  const suf = activeSuffix();
  try {
    const raw = suf
      ? localStorage.getItem('oc_v9_seen' + suf)
      : (localStorage.getItem('oc_v9_seen') ?? localStorage.getItem('oc_v8_seen'));
    seen = JSON.parse(raw ?? '{}') as Record<string, number>;
  } catch {
    seen = {};
  }
}

function saveSeen(): void {
  try {
    localStorage.setItem('oc_v9_seen' + activeSuffix(), JSON.stringify(seen));
  } catch {
    /* ignore */
  }
  scheduleSync();
}

export function hasSeen(gid: GameId): boolean {
  return !!seen[gid];
}

export function markSeen(gid: GameId): void {
  seen[gid] = 1;
  saveSeen();
}

/** Découverte d'un défi précis d'un jeu (intro de défi) — clé `jeu#défi`. */
export function hasSeenDefi(gid: GameId, defi: number): boolean {
  return !!seen[`${gid}#${defi}`];
}

export function markSeenDefi(gid: GameId, defi: number): void {
  seen[`${gid}#${defi}`] = 1;
  saveSeen();
}

/**
 * Purge les données de jeu du PROFIL ACTIF (progression, scores, « déjà vu »).
 * Ne touche ni les réglages de l'appareil ni les autres profils. F-SETTINGS + F-PROFILE-002.
 */
export function clearAllData(): void {
  const suf = activeSuffix();
  const keys = suf
    ? ['oc_v9' + suf, 'oc_v9_seen' + suf, 'oc_v10_score' + suf]
    : ['oc_v9', 'oc_v8', 'oc_v9_seen', 'oc_v8_seen', 'oc_v10_score', 'oc_v9_score', 'oc_v8_score'];
  try {
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* stockage indisponible : rien à purger */
  }
  progress = {};
  seen = {};
}
